import React from "react";

import { css } from "@emotion/react";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { CSS_MQ } from "../../styles/breakpoints";
import { ContentContainer } from "../_shared-components/arrangement/Container";
import { ContentfulComponentCMSHook } from "@swo/shared";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import styled from "@emotion/styled";
import { hyperlinksOptions, renderTextOptions, transformJson } from "../../utils/contentful/rich-text-formatting";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { richTextStyles } from "../../styles/rich-text";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { responsivePlainTextStyles } from "../../styles/text-styles";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { ImageDetailsCtaType } from "@swo/storefront";
import { SORichTextRenderer } from "../_shared-components/so-rich-text";
import { ContentfulImage2 } from "../_shared-components/image-related/ContentfulImage2";
import cn from "classnames";

export const ImageDetailsCtaComponent: ReactTFC<ImageDetailsCtaType & ContentfulComponentCMSHook> = props => {
  return (
    <ContentContainer className={cn(getComponentCMSClassname(props?.sys?.id), "u-relative")}>
      <ContentfulImage2
        assetId={props.image.image.sys.id}
        contentfulUrl={props.image.image.url}
        cropping={props.image}
        sizes="(max-width: 1399px) 100vw, 1400px"
        loading={"lazy"}
        css={css`
          position: relative;

          max-height: 600px;
          width: 100%;
          display: flex;
        `}
        withTransparency={false}
      />

      <div
        css={css`
          padding-top: 20px;

          ${CSS_MQ.atLeastMd} {
            display: grid;
            grid-auto-columns: 1fr;
            grid-auto-flow: column;

            padding-top: 30px;
            padding-left: 40px;
            padding-right: 40px;
          }

          ${CSS_MQ.atLeastLg} {
            padding-top: 50px;
            padding-left: 64px;
            padding-right: 64px;
          }
        `}
      >
        <div
          css={css`
            ${CSS_MQ.atLeastMd} {
              margin-right: 16px;
            }
          `}
        >
          <h2 css={descriptiveStyles}>
            <EXPERIMENTAL_TextWithProductNameRenderer text={props.descriptiveHeadline} />
          </h2>
          <h3 css={emotionalStyles}>
            <EXPERIMENTAL_TextWithProductNameRenderer text={props.emoHeadline} />
          </h3>
        </div>
        <div>
          <Hr />
          <div
            css={[
              FONTS.SwarovskiFutura300,
              fontColor.black,
              responsivePlainTextStyles,
              richTextStyles,
              css`
                & + * {
                  margin-top: 23px;
                  ${CSS_MQ.atLeastMd} {
                    margin-top: 36px;
                  }
                }
              `,
            ]}
          >
            <SORichTextRenderer
              richText={props.richText}
              options={{ ...hyperlinksOptions, renderText: renderTextOptions }}
            />
          </div>

          {props.callToAction && (
            <SwarovskiButton
              as={"a"}
              link={props.callToAction.data}
              isInternal={props.callToAction.type === "Internal Link"}
              variant={"outline"}
              target={props.callToAction.openInNewTab ? "_blank" : undefined}
            >
              {props.callToAction.label}
            </SwarovskiButton>
          )}
        </div>
      </div>
    </ContentContainer>
  );
};

const descriptiveStyles = [
  FONTS.SwarovskiFutura500,
  fontSize.x2,
  css`
    text-align: left;

    padding-bottom: 2px;
    ${CSS_MQ.atLeastMd} {
      padding-bottom: 5px;
    }

    color: ${CSS_COLORS.Black};
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 0;
  `,
];

const emotionalStyles = [
  FONTS.MinervaModern500,
  fontSize.x5x,
  css`
    word-break: break-word;

    color: ${CSS_COLORS.Green};
    text-transform: uppercase;
    margin-bottom: 15px;
    margin-top: 0;

    ${CSS_MQ.atLeastLg} {
      margin-right: 20px;
    }
  `,
];

export const Hr = styled.hr`
  margin: 0;
  width: 20px;

  height: 1px;

  border: none;
  background-color: ${CSS_COLORS.Black};
`;
